import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { ProgresoCircular, Semaforo } from '@xiltrion/ui/bloques/indicadores';
import { Tabla } from '@xiltrion/ui/bloques/tabla';
import { SelectorFecha, fechaAIso } from '@xiltrion/ui/bloques/selectores';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { fondoEtiqueta } from '@xiltrion/ui/paleta';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { Tabs, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { cn } from '@xiltrion/ui/utils';
import { apiGet, apiPost } from '@/api/client';
import { avisarError, useProjects } from '@/api/hooks';
import type { ProjectDetail } from '@/api/types';
import { useSession, useTerms } from '@/app/session';
import { formatDate } from '../tasks/view-logic';

interface Template { id: string; code: string | null; name: string; description: string | null; system: boolean; module: string; sections: string[]; views: string[] }

export function ProjectsList() {
  const navigate = useNavigate();
  const { can } = useSession();
  const { t, nuevo } = useTerms();
  const [archived, setArchived] = useState(false);
  const { data = [], isLoading } = useProjects(archived);
  // `?nuevo=1` (atajo «+» del menú lateral) abre el diálogo; así también sirve como enlace.
  const [params, setParams] = useSearchParams();
  const creating = params.get('nuevo') === '1';
  const setCreating = (open: boolean) => setParams((p) => { if (open) p.set('nuevo', '1'); else p.delete('nuevo'); return p; }, { replace: true });
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo={t('project', true)} descripcion="Todo lo que tu equipo está haciendo, con su avance y semáforo." acciones={can('project.create') && <Button onClick={() => setCreating(true)}><Icono nombre="mdi:plus" />{nuevo('project')}</Button>} />
      <Tabs value={archived ? 'archived' : 'active'} onValueChange={(v) => setArchived(v === 'archived')}><TabsList><TabsTrigger value="active">Activos</TabsTrigger><TabsTrigger value="archived">Archivados</TabsTrigger></TabsList></Tabs>
      {!isLoading && data.length === 0 ? (
        <EstadoVacio titulo={archived ? 'No hay proyectos archivados' : `Aún no hay ${t('project', true).toLowerCase()}`} descripcion="Crea uno desde una plantilla o en blanco." accion={can('project.create') && !archived ? <Button onClick={() => setCreating(true)}>{nuevo('project')}</Button> : undefined} borde />
      ) : (
        <Tabla filas={data} claveFila={(p) => p.id} cargando={isLoading} onClicFila={(p) => navigate(`/proyectos/${p.id}`)}
          columnas={[
            { key: 'name', titulo: t('project'), valor: (p) => p.name, render: (p) => <span className="inline-flex items-center gap-2 font-medium"><span className={cn('flex size-6 items-center justify-center rounded text-white', fondoEtiqueta(p.color))}><Icono nombre={p.icon as NombreIcono} className="size-3.5" /></span>{p.name}</span> },
            { key: 'progress', titulo: 'Avance', valor: (p) => p.progress, render: (p) => <span className="inline-flex items-center gap-2"><ProgresoCircular valor={p.progress} tamano={30} grosor={3} color={p.trafficLight} /><Semaforo color={p.trafficLight} /></span>, ordenable: true },
            { key: 'tasks', titulo: t('task', true), valor: (p) => p.stats.total, numerico: true, render: (p) => `${p.stats.done}/${p.stats.total}` },
            { key: 'overdue', titulo: 'Vencidas', valor: (p) => p.stats.overdue, numerico: true, render: (p) => <span className={cn(p.stats.overdue > 0 && 'font-medium text-destructive')}>{p.stats.overdue}</span> },
            { key: 'due', titulo: 'Fin', valor: (p) => p.dueDate ?? '', render: (p) => formatDate(p.dueDate, true) || '—', unaLinea: true },
          ]} />
      )}
      {creating && <NewProjectDialog onClose={() => setCreating(false)} onCreated={(p) => navigate(`/proyectos/${p.id}`)} />}
    </div>
  );
}

/** Crear un proyecto: en blanco, desde una plantilla (del núcleo o de un paquete) o de un tipo registrado por un paquete. */
export function NewProjectDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (p: ProjectDetail) => void }) {
  const { me } = useSession();
  const { t, nuevo } = useTerms();
  const { data: templates = [] } = useQuery({ queryKey: ['templates'], queryFn: () => apiGet<Template[]>('/projects/templates') });
  const types = (me?.extensions ?? []).flatMap((e) => e.projectTypes);
  const [name, setName] = useState('');
  const [choice, setChoice] = useState('blank');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = () => {
    if (!name.trim()) return;
    setSaving(true);
    const body: Record<string, unknown> = { name: name.trim(), startDate: fechaAIso(startDate), dueDate: fechaAIso(dueDate) };
    if (choice.startsWith('tpl:')) body.templateId = choice.slice(4);
    if (choice.startsWith('type:')) body.typeCode = choice.slice(5);
    void apiPost<ProjectDetail>('/projects', body).then((p) => { onCreated(p); onClose(); }, (e) => { setSaving(false); avisarError(e); });
  };
  const opciones = [
    { value: 'blank', title: 'En blanco', desc: 'Por hacer, en curso y terminado', icon: 'mdi:folder-outline' },
    ...types.map((ty) => ({ value: `type:${ty.code}`, title: ty.label, desc: ty.description, icon: ty.icon })),
    ...templates.filter((tp) => !tp.code?.startsWith('TYPE_') && tp.code !== 'BASIC').map((tp) => ({ value: `tpl:${tp.id}`, title: tp.name, desc: tp.description ?? tp.sections.join(' · '), icon: tp.system ? 'mdi:clipboard-list-outline' : 'mdi:content-copy' })),
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>{nuevo('project')}</DialogTitle><DialogDescription>Elige cómo empezar; las secciones y vistas se pueden cambiar después.</DialogDescription></DialogHeader>
        <FieldGroup>
          <Field><FieldLabel htmlFor="np">Nombre</FieldLabel><Input id="np" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} placeholder={`Nombre del ${t('project').toLowerCase()}`} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field><FieldLabel>Inicio (opcional)</FieldLabel><SelectorFecha value={startDate} onChange={setStartDate} limpiable /></Field>
            <Field><FieldLabel>Fin (opcional)</FieldLabel><SelectorFecha value={dueDate} onChange={setDueDate} limpiable /></Field>
          </div>
          <RadioGroup value={choice} onValueChange={setChoice} className="grid max-h-72 gap-2 overflow-y-auto sm:grid-cols-2">
            {opciones.map((o) => (
              <FieldLabel key={o.value} htmlFor={o.value} className="cursor-pointer rounded-md border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value={o.value} id={o.value} className="mt-0.5" />
                  <Icono nombre={o.icon as NombreIcono} className="mt-0.5 text-muted-foreground" />
                  <div className="min-w-0"><div className="text-sm font-medium">{o.title}</div><div className="line-clamp-2 text-xs font-normal text-muted-foreground">{o.desc}</div></div>
                </div>
              </FieldLabel>
            ))}
          </RadioGroup>
        </FieldGroup>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={submit} disabled={!name.trim() || saving}>{saving ? 'Creando…' : 'Crear'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
